import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import TarotCard from '@/components/TarotCard';

/**
 * 3D-flip wrapper around TarotCard. Shows the deck's back illustration
 * (cards/_back.jpeg) first; after the optional auto-reveal delay or
 * a tap, flips on its Y-axis to reveal the actual card.
 *
 * Used in spread screens (Reading.tsx) to turn what used to be an
 * instant render into a small ritual — cards "land" face-down, then
 * turn over one by one. Subtle but the difference between a results
 * screen and a tarot reading.
 *
 * Implementation notes:
 *   • Two faces share the same 3D-preserved parent; one is at rotateY
 *     0° (back), the other at 180° (front). Animating the parent's
 *     rotateY between 0 and 180 hands visibility off thanks to
 *     `backfaceVisibility: hidden` on each face.
 *   • TarotCard is passed `noIntro` because the wrapper owns the flip.
 *     Without it the card would visibly spin twice — once due to its
 *     own mount animation, then again with the parent flip.
 *   • If a `revealDelayMs` is given, the card flips automatically after
 *     that delay. Useful for cascade reveals (n × 250ms per slot). If
 *     omitted, the card waits for a tap.
 */
interface Props {
  card: unknown;
  size?: number;
  /** Auto-flip after this many ms. If omitted, the card stays face-down
   *  until the user taps it. */
  revealDelayMs?: number;
}

export default function FlippableCard({
  card,
  size = 220,
  revealDelayMs,
}: Props) {
  const [revealed, setRevealed] = useState(false);

  const w = size;
  // Match the natural aspect ratio of our card asset files (768×1028 →
  // 1.339). Must stay in sync with TarotCard.tsx — both render into
  // boxes that show the same image.
  const h = Math.round(size * (1028 / 768));

  // Auto-reveal timer. Skip when card is already revealed (e.g. the
  // parent re-rendered for some other reason).
  useEffect(() => {
    if (revealed) return;
    if (revealDelayMs === undefined) return;
    const handle = window.setTimeout(
      () => setRevealed(true),
      Math.max(0, revealDelayMs),
    );
    return () => window.clearTimeout(handle);
  }, [revealDelayMs, revealed]);

  const onTap = () => {
    if (!revealed) setRevealed(true);
  };

  return (
    <div
      className="mx-auto select-none"
      style={{ perspective: 1200, width: w, height: h }}
    >
      <motion.div
        onClick={onTap}
        animate={{ rotateY: revealed ? 180 : 0 }}
        transition={{ duration: 0.85, ease: [0.4, 0.0, 0.2, 1] }}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          transformStyle: 'preserve-3d',
          cursor: revealed ? 'default' : 'pointer',
        }}
      >
        {/* Back face — visible while rotateY ≈ 0 */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <img
            src="/cards/_back.jpeg"
            alt=""
            width={w}
            height={h}
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </div>

        {/* Front face — pre-rotated 180° so it becomes visible after the
            parent's rotateY hits 180°. */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <TarotCard card={card} size={size} noIntro />
        </div>
      </motion.div>
    </div>
  );
}
